import { useCallback, useEffect, useRef, useState } from "react";
import BarChartIcon from "@mui/icons-material/BarChart";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import PercentIcon from "@mui/icons-material/Percent";
import {
  buscarPreviasPisCofins,
  buscarPreviasPisCofinsPorTexto,
  criarNovaPreviaPisCofins,
  editarPreviasPisCofins,
  atualizarSelicPreviaPisCofins,
} from "../../services/api-piscofins.service";
import TableCardv2 from "../../components/TableCardv2";
import { Fab } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { debounce } from "lodash";
import { formatValue } from "../../utils/formatValue";
import styles from "./styles.module.css";
import SnackBar from "../../components/SnackBar";
import SnackBarProgresso from "../../components/SnackBarProgresso";
import ModalVersoes from "./ModalVersoes";
import ModalVersoesProjeto from "../../components/Projeto/ModalVersoes";
import useClientPolling from "../../hooks/useClientPolling";
import {
  PreviaPisCofinsProvider,
  usePreviaPisCofins,
} from "./PreviaPisCofinsContext";
import ModalSelecaoEmpresa from "../../components/ModalSelecaoEmpresa";
import ModalConfirmacao from "../../components/ModalConfirmacao";
import {
  usePermissoes,
  CHAVES_PIS_COFINS_VIEW,
  CHAVES_PIS_COFINS_EDIT,
  CHAVES_PIS_COFINS_IMPORT,
} from "../../config/Context/PermissoesContext";

// Componente de Progresso - Usando SnackBarProgresso com Client Polling
const ProgressoComponent = () => {
  const {
    isConnected,
    progresso,
    erro,
    resultado,
    loading,
    criarVersaoComMotor,
    executarMotor,
    desconectar,
    limparEstado,
  } = useClientPolling();

  // Função para mapear etapas para números (atualizado com etapas do backend)
  const mapearEtapa = useCallback((etapa) => {
    const etapas = {
      validacao: 1,
      busca_grupo: 2,
      busca_empresas: 3,
      validacao_schemas: 4,
      criacao: 5,
      execucao_motores: 6,
      consolidacao: 7,
      concluido: 8,
      erro: 0,
    };
    return etapas[etapa] || 1;
  }, []);

  // Converter dados do Client Polling para o formato do SnackBarProgresso
  const [progressData, setProgressData] = useState(null);

  useEffect(() => {
    if (!progresso) {
      setProgressData(null);
      return;
    }

    // ✅ CORREÇÃO COMPLETA: Mapeamento de dados do progresso
    // Os dados vêm de progresso.status (estrutura da API)
    const data = {
      titulo:
        progresso.status?.mensagem ||
        progresso.mensagem ||
        "Executando motores...",
      progresso: progresso.status?.progresso || progresso.progresso || 0,
      etapaAtual: mapearEtapa(progresso.status?.etapa || progresso.etapa) || 1,
      totalEtapas: 8, // Total de etapas conhecidas

      // ✅ Informações detalhadas do backend
      empresaAtual: progresso.status?.empresaAtual,
      motorAtual: progresso.status?.motorAtual,
      empresasProcessadas: progresso.status?.empresasProcessadas,
      totalEmpresas: progresso.status?.totalEmpresas,
      motoresProcessados: progresso.status?.motoresProcessados,
      totalMotores: progresso.status?.totalMotores,

      descricao:
        progresso.status?.descricao ||
        progresso.descricao ||
        progresso.status?.mensagem,
      tipoMotor: "PIS-COFINS",
      tempoEstimado: progresso.tempoEstimado || "Calculando...",
      sucesso: erro ? false : resultado ? true : undefined,
      erro: erro ? true : false,
      etapaOriginal: progresso.status?.etapa || progresso.etapa,
      detalhes: progresso.detalhes,
    };

    setProgressData(data);
  }, [progresso, erro, resultado, mapearEtapa]);

  // Controlar visibilidade dos componentes
  const showProgress = isConnected && (progresso || erro || resultado);
  const showLoading = loading && !erro && !resultado;

  // Limpar estado quando componente desmontar
  useEffect(() => {
    return () => {
      desconectar();
      limparEstado();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vazio: executa cleanup APENAS ao desmontar o componente

  // Expor funções para uso externo
  useEffect(() => {
    window.criarVersaoComMotorPISCOFINS = criarVersaoComMotor;
    window.executarMotorPISCOFINS = executarMotor;
    return () => {
      delete window.criarVersaoComMotorPISCOFINS;
      delete window.executarMotorPISCOFINS;
    };
  }, [criarVersaoComMotor, executarMotor]);

  return (
    <>
      <SnackBarProgresso
        open={showProgress}
        onClose={() => {
          desconectar();
          limparEstado();
        }}
        progressData={progressData}
        showLoading={showLoading}
      />
    </>
  );
};

const header = {
  icon: <BarChartIcon fontSize="1rem" />,
  title: "Prévias PIS/COFINS",
};

const columns = [
  { key: "razaoSocial", label: "Razão Social" },
  { key: "cnpj", label: "CNPJ" },
  { key: "dtInicial", label: "Dt Inicial" },
  { key: "dtFinal", label: "Dt Final" },
  { key: "status", label: "Status" },
];

const PreviaPISCOFINS = () => {
  const { pode, carregado, chaves } = usePermissoes();
  const handleOrganizaDadosRef = useRef((raw) => raw);
  const [previas, setPrevias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAnalisar, setModalAnalisar] = useState({});
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [modalVersoes, setModalVersoes] = useState({
    open: false,
    idPrevia: null,
    isProjeto: false,
  });
  const [modalVersoesProjeto, setModalVersoesProjeto] = useState({
    open: false,
    idPrevia: null,
  });
  const [modalSelecaoEmpresa, setModalSelecaoEmpresa] = useState({
    open: false,
  });
  const [modalCancelarPrevia, setModalCancelarPrevia] = useState({
    open: false,
    idPrevia: null,
  });

  // Ler intenção de reabrir ModalVersoes vinda de outras telas (Base Legal / Resumo Geral)
  const {
    reopenModalVersoesConfig,
    clearReopenModalVersoesConfig,
  } = usePreviaPisCofins();

  const handleSnackBar = useCallback((obj) => {
    setObjSnackBar(obj);
  }, []);

  const handleClick = useCallback((rowOrId, isProjeto = false) => {
    const idPrevia =
      rowOrId && typeof rowOrId === "object"
        ? rowOrId.idPreviaPisCofins ?? rowOrId.idPrevia
        : rowOrId;
    setModalVersoes({
      open: true,
      idPrevia,
      isProjeto,
    });
    if (rowOrId && typeof rowOrId === "object") {
      setModalAnalisar({ ...rowOrId, idPreviaPisCofins: idPrevia ?? rowOrId.idPreviaPisCofins });
    }
  }, []);

  const handleModalAnalisar = useCallback(async (data) => {
    sessionStorage.setItem("cnpjSelecionado", data.cnpj);

    setModalVersoes({
      open: true,
      idPrevia: data.idPreviaPisCofins,
      isProjeto: false,
    });
    setModalAnalisar(data);
  }, []);

  const handleAbrirModalCancelar = useCallback((row) => {
    setModalCancelarPrevia({ open: true, idPrevia: row.idPreviaPisCofins });
  }, []);
  const handleFecharModalCancelar = useCallback(() => {
    setModalCancelarPrevia({ open: false, idPrevia: null });
  }, []);

  const handleOrganizaDados = useCallback((dados) => {
    const btnAnalisar = (row) => {
      if (!pode(CHAVES_PIS_COFINS_VIEW)) return null;
      return {
        icon: <QueryStatsIcon />,
        title: "Analisar",
        onClick: () => handleModalAnalisar(row),
        disabled: row.status === "PREVIA-PENDENTE" || row.status === "PREVIA-PROCESSANDO",
      };
    };
    const btnAtualizarSelic = (row) => ({
      icon: <PercentIcon sx={{ color: "#2e7d32" }} />,
      title: "Atualizar Selic",
      onClick: async () => {
        const id = row.idPreviaPisCofins ?? row.idPrevia;
        if (!id) return;
        setLoading(true);
        try {
          await atualizarSelicPreviaPisCofins(id);
          handleSnackBar({ type: "success", msg: "SELIC atualizada com sucesso." });
          const responsePrevias = await buscarPreviasPisCofins();
          setPrevias(handleOrganizaDados(responsePrevias.previas));
        } catch (error) {
          handleSnackBar({
            type: "error",
            msg:
              error.response?.data?.mensagem ||
              "Erro ao atualizar SELIC. Tente novamente.",
          });
        } finally {
          setLoading(false);
        }
      },
      disabled: row.status === "PREVIA-PENDENTE" || row.status === "PREVIA-PROCESSANDO",
      variant: "success",
    });
    const btnVizualizar = (row) => {
      if (!pode(CHAVES_PIS_COFINS_VIEW)) return null;
      return {
        icon: <VisibilityIcon />,
        title: "Visualizar",
        onClick: () => handleClick(row.idPreviaPisCofins, false),
        variant: "primary",
      };
    };
    const btnCancelar = (row) => {
      if (!pode(CHAVES_PIS_COFINS_EDIT)) return null;
      return {
        icon: <CancelIcon />,
        title: "Cancelar",
        onClick: () => handleAbrirModalCancelar(row),
        variant: "danger",
      };
    };
    const formatStatus = (status) => {
      switch (status) {
        case "PREVIA-PENDENTE":
          return (
            <span className={styles.status_container}>
              <span className={styles.pendente} /> Na fila
            </span>
          );
        case "PREVIA-PROCESSANDO":
          return (
            <span className={styles.status_container}>
              <span className={styles.pendente} /> Processando
            </span>
          );
        case "PREVIA-ANDAMENTO":
          return (
            <span className={styles.status_container}>
              <span className={styles.andamento} /> Andamento
            </span>
          );
        case "PROJETO-INICIADO":
          return (
            <span className={styles.status_container}>
              <span className={styles.aprovada} /> Projeto Iniciado
            </span>
          );
        case "PROJETO-APROVADO":
          return (
            <span className={styles.status_container}>
              <span className={styles.aprovada} /> Projeto Aprovado
            </span>
          );
        case "PREVIA-CANCELADA":
          return (
            <span className={styles.status_container}>
              <span className={styles.cancelada} /> Cancelada
            </span>
          );
        default:
          return (
            <span className={styles.status_container}>
              <span className={styles.andamento} /> {status || "Andamento"}
            </span>
          );
      }
    };

    return dados.map((dado) => {
      // Objeto único da linha: normaliza id (API pode retornar idPrevia ou idPreviaPisCofins) e garante dados completos para modal/contexto
      const rowData = {
        ...dado,
        idPreviaPisCofins: dado.idPreviaPisCofins ?? dado.idPrevia,
      };
      const formatBtn = (status) => {
        switch (status) {
          case "PREVIA-ANDAMENTO":
            return [btnAnalisar(rowData), btnAtualizarSelic(rowData), btnCancelar(rowData)].filter(Boolean);
          case "PROJETO-INICIADO":
          case "PROJETO-APROVADO":
            return [btnVizualizar(rowData), btnCancelar(rowData)].filter(Boolean);
          case "PREVIA-CANCELADA":
            return [btnVizualizar(rowData)].filter(Boolean);
          default:
            return [btnAnalisar(rowData)].filter(Boolean);
        }
      };
      return {
        ...rowData,
        razaoSocial: rowData.razaoSocial,
        cnpj: formatValue("cnpj", rowData.cnpj),
        dtInicial: formatValue("data", rowData.dtInicial),
        dtFinal: formatValue("data", rowData.dtFinal),
        status: formatStatus(rowData.status),
        btn: formatBtn(rowData.status),
      };
    });
  }, [pode, handleModalAnalisar, handleClick, handleAbrirModalCancelar]);

  handleOrganizaDadosRef.current = handleOrganizaDados;

  const handleInititalData = useCallback(async () => {
    setLoading(true);

    try {
      const responsePrevias = await buscarPreviasPisCofins();
      if (responsePrevias?.sucesso === false) {
        handleSnackBar({
          type: "error",
          msg:
            responsePrevias?.mensagem ||
            "Erro ao carregar dados de PIS/COFINS",
        });
        setPrevias([]);
        return;
      }
      const lista = responsePrevias?.previas;
      if (!Array.isArray(lista)) {
        handleSnackBar({
          type: "error",
          msg:
            responsePrevias?.mensagem ||
            "Resposta inválida da API de prévias PIS/COFINS.",
        });
        setPrevias([]);
        return;
      }
      setPrevias(handleOrganizaDadosRef.current(lista));
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.message ||
          "Erro ao carregar dados de PIS/COFINS",
      });
      setPrevias([]);
    } finally {
      setLoading(false);
    }
  }, [handleSnackBar]);

  const handleConfirmarCancelar = useCallback(async () => {
    const id = modalCancelarPrevia.idPrevia;
    if (!id) return;
    try {
      await editarPreviasPisCofins(id, { status: "PREVIA-CANCELADA" });
      handleSnackBar({ type: "success", msg: "Prévia cancelada com sucesso" });
      handleFecharModalCancelar();
      await handleInititalData();
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          "Erro ao cancelar prévia. Tente novamente.",
      });
    }
  }, [
    modalCancelarPrevia.idPrevia,
    handleFecharModalCancelar,
    handleInititalData,
    handleSnackBar,
  ]);

  // ✅ NOVA FUNCIONALIDADE: Função global para simular clique no botão Analisar
  useEffect(() => {
    window.simularCliqueAnalisar = (
      idPrevia,
      tipoPrevia,
      isProjeto,
      dadosEmpresa
    ) => {
      // ✅ REABRIR MODAL DE VERSÕES COM LOADING
      setModalVersoes({
        open: true,
        idPrevia: idPrevia,
        isProjeto: isProjeto || false,
      });

      // ✅ ATUALIZAR DADOS DO MODAL ANALISAR SE NECESSÁRIO
      if (dadosEmpresa) {
        setModalAnalisar(dadosEmpresa);
      }
    };

    return () => {
      delete window.simularCliqueAnalisar;
    };
  }, []);

  useEffect(() => {
    if (!carregado) return;
    handleInititalData();
  }, [carregado, chaves, handleInititalData]);

  // Reabrir automaticamente a ModalVersoes quando houver configuração vinda do contexto
  useEffect(() => {
    if (!reopenModalVersoesConfig?.idPreviaPisCofins) {
      return;
    }

    setModalVersoes({
      open: true,
      idPrevia: reopenModalVersoesConfig.idPreviaPisCofins,
      isProjeto: false,
    });

    if (reopenModalVersoesConfig.dadosEmpresa) {
      setModalAnalisar((prev) => ({
        ...prev,
        ...reopenModalVersoesConfig.dadosEmpresa,
      }));
    }

    clearReopenModalVersoesConfig();
  }, [reopenModalVersoesConfig, clearReopenModalVersoesConfig]);

  // Função para reabrir o modal após criar versão
  const handleReopenModalVersoes = () => {
    // Usar os dados da prévia atual (modalVersoes) em vez de modalAnalisar
    if (modalVersoes.idPrevia) {
      // Criar dados da prévia atual para reabrir o modal
      const dadosPreviaAtual = {
        idPreviaPisCofins: modalVersoes.idPrevia,
        razaoSocial: modalAnalisar?.razaoSocial || "Prévia Atual",
        status: modalAnalisar?.status || "PREVIA-ANDAMENTO",
        cnpj: modalAnalisar?.cnpj || "00000000000000",
        // Adicionar outros campos necessários se existirem
      };

      handleModalAnalisar(dadosPreviaAtual);
    } else {
      // Tentar usar modalAnalisar como fallback
      if (modalAnalisar?.idPreviaPisCofins) {
        handleModalAnalisar(modalAnalisar);
      }
    }
  };

  const handlePesquisaPorTexto = async (texto) => {
    setLoading(true);
    try {
      const responsePrevias = await buscarPreviasPisCofinsPorTexto(texto);
      if (responsePrevias?.sucesso === false) {
        handleSnackBar({
          type: "error",
          msg: responsePrevias?.mensagem || "Erro ao buscar prévias.",
        });
        setPrevias([]);
        return;
      }
      const lista = responsePrevias?.previas;
      if (!Array.isArray(lista)) {
        handleSnackBar({
          type: "error",
          msg: responsePrevias?.mensagem || "Resposta inválida da API.",
        });
        setPrevias([]);
        return;
      }
      setPrevias(handleOrganizaDadosRef.current(lista));
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.message ||
          "Ops, erro ao buscar prévias.",
      });
      setPrevias([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDebounce = debounce(handlePesquisaPorTexto, 300);

  // Função para abrir modal de seleção de empresa
  const handleAbrirModalSelecaoEmpresa = () => {
    setModalSelecaoEmpresa({ open: true });
  };

  // Função auxiliar para formatar data para YYYY-MM-DD (apenas data, sem hora)
  const formatarDataParaAPI = (data) => {
    if (!data) return null;
    const date = new Date(data);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Função para confirmar seleção de empresa
  const handleConfirmarSelecaoEmpresa = async (dadosSelecao) => {
    setModalSelecaoEmpresa({ open: false });

    const {
      idGrupoEconomico,
      nomeGrupo,
      dtInicial,
      dtFinal,
      tiposPreviaIds = [],
    } = dadosSelecao || {};

    // Validações
    if (!idGrupoEconomico) {
      handleSnackBar({
        type: "error",
        msg: "Grupo econômico não selecionado",
      });
      return;
    }

    if (!dtInicial || !dtFinal) {
      handleSnackBar({
        type: "error",
        msg: "Período não preenchido completamente",
      });
      return;
    }

    if (!tiposPreviaIds || tiposPreviaIds.length === 0) {
      handleSnackBar({
        type: "error",
        msg: "Selecione ao menos um tipo de prévia",
      });
      return;
    }

    try {
      setLoading(true);

      // Formatar datas para YYYY-MM-DD (apenas data, sem hora)
      const dtInicialFormatada = formatarDataParaAPI(dtInicial);
      const dtFinalFormatada = formatarDataParaAPI(dtFinal);

      const payload = {
        idGrupoEconomico: Number(idGrupoEconomico),
        dtInicial: dtInicialFormatada,
        dtFinal: dtFinalFormatada,
        tiposPreviaIds: tiposPreviaIds.map((id) => Number(id)),
      };

      const response = await criarNovaPreviaPisCofins(payload);

      if (response?.sucesso === false) {
        handleSnackBar({
          type: "error",
          msg:
            response?.mensagem ||
            "Não foi possível criar a prévia.",
        });
        return;
      }

      handleSnackBar({
        type: "success",
        msg: `Prévia criada com sucesso para o grupo ${nomeGrupo}!`,
      });

      // Recarregar a lista de prévias
      await handleInititalData();
    } catch (error) {
      console.error("[Prévia PIS/COFINS] Erro ao criar prévia:", error);
      const data = error.response?.data;
      const status = error.response?.status;
      let msg =
        data?.mensagem ||
        (typeof data?.error === "string" ? data.error : null) ||
        error.message ||
        "Erro ao criar prévia. Tente novamente.";
      if (status === 401) {
        msg =
          data?.mensagem ||
          "Sessão inválida ou expirada (401). Faça login novamente e confira se a API PIS/COFINS consegue chamar a API de autenticação.";
      }
      handleSnackBar({
        type: "error",
        msg,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PreviaPisCofinsProvider>
      <div className={styles.container}>
        <TableCardv2
          header={header}
          columns={columns}
          data={previas}
          debounce={handleDebounce}
          loading={loading}
        />
      </div>

      {pode(CHAVES_PIS_COFINS_IMPORT) && (
        <Fab
          sx={{
            background: "#4b6fc0",
            bottom: 40,
            right: 20,
            position: "fixed",
            zIndex: 1000,
            "&:hover": {
              backgroundColor: "#4b6fc0",
            },
          }}
          aria-label="add"
          onClick={handleAbrirModalSelecaoEmpresa}
        >
          <AddIcon sx={{ color: "#fff" }} />
        </Fab>
      )}

      <ModalVersoes
        open={modalVersoes.open}
        onClose={() =>
          setModalVersoes({ open: false, idPrevia: null, isProjeto: false })
        }
        idPrevia={modalVersoes.idPrevia}
        tipoPrevia="PIS-COFINS"
        isProjeto={modalVersoes.isProjeto}
        contextHook={usePreviaPisCofins}
        dadosEmpresa={modalAnalisar}
        onDataUpdated={handleInititalData}
        onReopenModal={() => {
          handleReopenModalVersoes();
        }}
      />

      <ModalVersoesProjeto
        open={modalVersoesProjeto.open}
        onClose={() => setModalVersoesProjeto({ open: false, idPrevia: null })}
        idPrevia={modalVersoesProjeto.idPrevia}
        tipoProjeto="PIS/COFINS"
        rechargeData={handleInititalData}
        dadosEmpresa={modalAnalisar} // Passar dados da empresa para criar crédito
      />

      <ModalSelecaoEmpresa
        open={modalSelecaoEmpresa.open}
        onClose={() => setModalSelecaoEmpresa({ open: false })}
        onConfirm={handleConfirmarSelecaoEmpresa}
        tipoPrevia="PIS-COFINS"
      />

      <ModalConfirmacao
        open={modalCancelarPrevia.open}
        onClose={handleFecharModalCancelar}
        onConfirm={handleConfirmarCancelar}
        title="Cancelar"
        message="Tem certeza que deseja cancelar? Esta prévia não será reaproveitada."
        confirmText="Confirmar"
        cancelText="Cancelar"
      />

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={handleSnackBar} />

      {/* Componente de Progresso */}
      <ProgressoComponent />
    </PreviaPisCofinsProvider>
  );
};

export default PreviaPISCOFINS;
